import { Context, createContext, ReactElement, useContext, useMemo, useState } from 'react';

import { Optional } from '@vimeo/core/types/generic';
import { EComposerSessionType, TComposerSessionId } from '@vimeo/live-core/types/composer_session';
import { CallablePlaceholder } from '@vimeo/live-core/types/general';

import { useInteractionToolsStatus } from './useInteractionToolsStatus';

interface IInteractionToolsStatusObserverContext {
  isApplicationAvailableForModeration: boolean;
  setIsApplicationAvailableForModeration(value: boolean): void;
}

export const InteractionToolsStatusObserverContext: Context<IInteractionToolsStatusObserverContext> =
  createContext<IInteractionToolsStatusObserverContext>({
    isApplicationAvailableForModeration: false,
    setIsApplicationAvailableForModeration: CallablePlaceholder,
  });

export function useInteractionToolsStatusObserverContext(): IInteractionToolsStatusObserverContext {
  return useContext(InteractionToolsStatusObserverContext);
}

interface IInteractionToolsStatusObserverProps {
  /**
   * Session type of interaction room.
   */
  sessionType: EComposerSessionType;
  /**
   * Session id of interaction room.
   */
  sessionId: TComposerSessionId;
  /**
   * Whether observer logic is active.
   */
  isActive?: boolean;
  /**
   * Whether to bypass status checks and show children component directly.
   */
  isBypass?: boolean;
  /**
   * Base component to manager with status checks.
   */
  children: ReactElement;
  /**
   * Component to show if interaction room status is disabled.
   */
  placeholder?: Optional<ReactElement>;
  /**
   * Whether application is available for moderation by owner or admin.
   */
  isAvailableForModeration?: boolean;
}

/**
 * For conditional rendering of chat and observing of room status.
 * Reduce load on firebase when widget is not expected to be active.
 */
export function InteractionToolsStatusObserver({
  isActive = false,
  isBypass = false,
  children,
  sessionId,
  sessionType,
  placeholder = null,
  isAvailableForModeration = false,
}: IInteractionToolsStatusObserverProps) {
  const [isApplicationAvailableForModeration, setIsApplicationAvailableForModeration] =
    useState<boolean>(isAvailableForModeration);

  const isInteractionToolsEnabled: boolean = useInteractionToolsStatus({
    isActive: isActive && !isBypass,
    sessionId,
    sessionType,
  });

  const interactionToolsStatusObserverContext: IInteractionToolsStatusObserverContext = useMemo(
    () => ({
      isApplicationAvailableForModeration,
      setIsApplicationAvailableForModeration,
    }),
    [isApplicationAvailableForModeration],
  );

  if (isBypass || isInteractionToolsEnabled || isApplicationAvailableForModeration) {
    return (
      <InteractionToolsStatusObserverContext.Provider value={interactionToolsStatusObserverContext}>
        {children}
      </InteractionToolsStatusObserverContext.Provider>
    );
  }

  return placeholder;
}
